import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/api/axios';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Download, FileText, FileImage, File, X, ExternalLink } from 'lucide-react';
import { getMediaUrl, mapMediaItems, normalizeProjectMediaList } from '../../utils/media';

function MarketingDocs() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [marketingDocs, setMarketingDocs] = useState([]);
  const [error, setError] = useState(null);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, doc: null });
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    const fetchMarketingDocs = async () => {
      if (!projectId) {
        setError('Project ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axiosInstance.get('/projects/media', {
          params: {
            project_id: parseInt(projectId), // Ensure project_id is sent as integer
            marketingMedia: true,
            
          }
        });

        // Check if response is an array and has data
        const normalizedDocs = mapMediaItems(
          normalizeProjectMediaList(response),
          'Marketing Document'
        ).map((item) => ({
          ...item,
          fileType: item.contentType || item.fileType,
          fileName: item.name || item.fileName || item.key,
          url: getMediaUrl(item),
        }));

        setMarketingDocs(normalizedDocs);
        setError(null);
      } catch (err) {
        console.error('Error fetching marketing documents:', err);
        const errorMessage = err.response?.data?.detail || 
                           err.response?.data?.message || 
                           'Failed to fetch marketing documents';
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketingDocs();
  }, [projectId]);

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <FileImage className="w-5 h-5 text-blue-500" />;
    } else if (fileType === 'application/pdf') {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return <FileText className="w-5 h-5 text-blue-600" />;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      return <FileText className="w-5 h-5 text-green-600" />;
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
      return <FileText className="w-5 h-5 text-orange-600" />;
    } else {
      return <File className="w-5 h-5 text-gray-500" />;
    }
  };

  const getFileTypeName = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return 'Image';
    } else if (fileType === 'application/pdf') {
      return 'PDF Document';
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      return 'Word Document';
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      return 'Excel Spreadsheet';
    } else if (fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
      return 'PowerPoint Presentation';
    } else {
      return 'Document';
    }
  };

  const handlePreview = (doc) => {
    const fileType = doc.fileType || doc.contentType;
    if (fileType?.startsWith('image/') || fileType === 'application/pdf') {
      setPreviewModal({ isOpen: true, doc });
    } else {
      // For non-previewable files, open in new tab
      window.open(getMediaUrl(doc), '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownload = async (doc) => {
    const docId = doc.id || doc.key;
    if (downloading[docId]) return;

    try {
      setDownloading(prev => ({ ...prev, [docId]: true }));
      
      const downloadUrl = getMediaUrl(doc);
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || doc.key || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleView = (doc) => {
    window.open(getMediaUrl(doc), '_blank', 'noopener,noreferrer');
  };

  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false, doc: null });
  };

  const PreviewModal = () => {
    if (!previewModal.isOpen || !previewModal.doc) return null;

    const { doc } = previewModal;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full relative">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">
              {doc.fileName || doc.key || 'Document Preview'}
            </h3>
            <button
              onClick={closePreviewModal}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
            {(doc.fileType || doc.contentType)?.startsWith('image/') ? (
              <img 
                src={getMediaUrl(doc)} 
                alt={doc.fileName || 'Preview'} 
                className="max-w-full h-auto mx-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                }}
              />
            ) : (doc.fileType || doc.contentType) === 'application/pdf' ? (
              <iframe
                src={`${getMediaUrl(doc)}#toolbar=0&navpanes=0&scrollbar=0`}
                className="w-full h-[70vh] border-0"
                title="PDF Preview"
                onError={() => {
                  toast.error('Unable to load PDF preview. Opening in new tab...');
                  window.open(getMediaUrl(doc), '_blank', 'noopener,noreferrer');
                  closePreviewModal();
                }}
              />
            ) : null}
          </div>
          
          <div className="flex justify-between items-center p-4 border-t bg-gray-50">
            <div className="flex items-center space-x-2">
              {getFileIcon(doc.fileType || doc.contentType)}
              <span className="text-sm text-gray-600">
                {getFileTypeName(doc.fileType)}
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handleView(doc)}
                className="inline-flex items-center space-x-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
              >
                <ExternalLink size={16} />
                <span>Open in New Tab</span>
              </button>
              <button
                onClick={() => handleDownload(doc)}
                disabled={downloading[doc.id || doc.key]}
                className="inline-flex items-center space-x-1 px-3 py-2 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors disabled:opacity-50"
              >
                <Download size={16} />
                <span>{downloading[doc.id || doc.key] ? 'Downloading...' : 'Download'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-flamingo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-4">
        <div className="text-red-500 mb-2">{error}</div>
        <button 
          onClick={() => window.location.reload()} 
          className="text-flamingo-500 hover:text-flamingo-600 font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Marketing Documents</h2>
        <button 
          onClick={() => window.location.reload()} 
          className="text-flamingo-500 hover:text-flamingo-600 font-medium"
        >
          Refresh
        </button>
      </div>
      
      {marketingDocs.length === 0 ? (
        <div className="text-center text-gray-500 py-8 bg-gray-50 rounded-lg">
          <svg 
            className="w-16 h-16 mx-auto text-gray-400 mb-4" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
            />
          </svg>
          <p>No marketing documents found for this project</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border">
          {/* Table Header */}
          <div className="border-b bg-gray-50">
            <div className="flex items-center px-6 py-3">
              <div className="flex-1 grid grid-cols-12 gap-4">
                <div className="col-span-8">
                  <span className="font-medium text-gray-700">Name</span>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-700">Type</span>
                </div>
                <div className="col-span-2 text-center">
                  <span className="font-medium text-gray-700">Actions</span>
                </div>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {marketingDocs.map((doc, index) => {
              const docId = doc.id || doc.key;
              
              return (
                <div 
                  key={docId} 
                  className="flex items-center px-6 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                    {/* Name Column */}
                    <div className="col-span-8 flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {getFileIcon(doc.fileType)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {doc.fileName || doc.key || `Document ${index + 1}`}
                        </p>
                      
                      </div>
                    </div>

                    {/* Type Column */}
                    <div className="col-span-2">
                      <span className="text-sm text-gray-900">
                        {getFileTypeName(doc.fileType || doc.contentType)}
                      </span>
                    </div>

                    {/* Actions Column */}
                    <div className="col-span-2 flex justify-center space-x-2">
                
                      <button
                        onClick={() => handleDownload(doc)}
                        disabled={downloading[docId]}
                        className="p-2 text-gray-600 hover:text-orange-600 transition-colors rounded-md hover:bg-orange-50 disabled:opacity-50"
                        title="Download"
                      >
                        <Download size={22} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      <PreviewModal />
    </div>
  );
}

export default MarketingDocs;